import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from './portalRoot';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    centered?: boolean;
    hideCloseButton?: boolean;
    staticBackdrop?: boolean;
    closeOnEsc?: boolean;
    closeOnOverlayClick?: boolean;
    className?: string;
    draggable?: boolean;
}

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    centered = true,
    hideCloseButton = false,
    staticBackdrop = false,
    closeOnEsc = true,
    closeOnOverlayClick = true,
    className = '',
    draggable = true
}: ModalProps) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    // Resolved once: a container that changes between renders remounts the modal (see portalRoot).
    const [portalRoot] = useState(getPortalRoot);

    useEffect(() => {
        // Prevent background scrolling when modal is open
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            // Reset position when modal is closed
            setPosition({ x: 0, y: 0 });
            setIsDragging(false);
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    // Handle keyboard events (ESC to close)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && closeOnEsc) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, closeOnEsc]);

    // Handle backdrop click — only close when the overlay itself is clicked, not modal content or
    // menus portaled to the body (e.g. react-select dropdowns), whose clicks bubble here through the
    // React tree even though their DOM lives outside the modal.
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlayClick && !staticBackdrop && e.target === e.currentTarget) {
            onClose();
        }
    };

    // Drag handlers
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!draggable || !title) return;

        setIsDragging(true);

        // Calculate the offset from the mouse position to the modal's top-left corner
        const rect = modalRef.current?.getBoundingClientRect();

        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
    };

    // Keyboard support for dragging
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!draggable || !title) return;

        const STEP = 10; // pixels to move per key press

        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault(); // Prevent scrolling

            setPosition((prev) => {
                const newPos = { ...prev };

                switch (e.key) {
                    case 'ArrowUp':
                        newPos.y -= STEP;
                        break;
                    case 'ArrowDown':
                        newPos.y += STEP;
                        break;
                    case 'ArrowLeft':
                        newPos.x -= STEP;
                        break;
                    case 'ArrowRight':
                        newPos.x += STEP;
                        break;
                }

                return newPos;
            });
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
        if (!isDragging) return;

        // Calculate new position based on mouse position and offset
        setPosition({
            x: e.clientX - dragOffset.x,
            y: e.clientY - dragOffset.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Add global mouse event listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove as unknown as (e: MouseEvent) => void);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove as unknown as (e: MouseEvent) => void);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove as unknown as (e: MouseEvent) => void);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, handleMouseMove, handleMouseUp]);

    if (!isOpen) return null;

    // Tailwind classes for modal size
    const modalSizeClass = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl'
    }[size];

    const modalContent = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
            role="presentation"
            onClick={handleBackdropClick}
        >
            <div
                aria-labelledby={title ? 'modal-title' : undefined}
                aria-modal="true"
                className={`relative ${modalSizeClass} w-full rounded-lg border border-gray-800 bg-[#121212] shadow-xl ${
                    centered && !isDragging ? 'my-auto' : 'my-10'
                } ${className} animate-[modalFadeIn_0.2s_ease-in-out]`}
                ref={modalRef}
                role="dialog"
                style={
                    isDragging || (draggable && position.x !== 0 && position.y !== 0)
                        ? {
                              position: 'absolute',
                              left: `${position.x}px`,
                              top: `${position.y}px`,
                              margin: 0,
                              transform: 'none'
                          }
                        : undefined
                }
            >
                {/* Header */}
                {title && (
                    <div
                        aria-label={draggable ? 'Drag to move modal' : undefined}
                        className={`flex items-center justify-between border-b border-gray-800 px-6 py-4 ${draggable ? 'cursor-move' : ''}`}
                        role={draggable ? 'button' : undefined}
                        tabIndex={draggable ? 0 : undefined}
                        onKeyDown={draggable ? handleKeyDown : undefined}
                        onMouseDown={draggable ? handleMouseDown : undefined}
                    >
                        <h3 className="text-lg font-medium text-white" id="modal-title">
                            {title}
                        </h3>
                        {!hideCloseButton && (
                            <button
                                aria-label="Close"
                                className="rounded-md p-1 text-gray-400 hover:bg-[#181818] hover:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                type="button"
                                onClick={onClose}
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                                </svg>
                            </button>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className={`px-6 py-4 ${!title ? 'pt-5' : ''}`}>{children}</div>

                {/* Footer */}
                {footer && <div className="border-t border-gray-800 px-6 py-4 flex justify-end gap-2">{footer}</div>}
            </div>
        </div>
    );

    // Using createPortal to render modal outside of the component hierarchy
    return createPortal(modalContent, portalRoot);
};

export default Modal;
